import { Invoice } from '../../../types';

export function ClassicInvoicePreview({ invoice, company, workspace }: { invoice: Invoice, company: any, workspace: any }) {
  // Currency formatting logic (copied from InvoiceTemplate)
  const currency = invoice.currency || workspace?.settings?.currency || 'USD';
  const getCurrencySymbol = (curr: string): string => {
    const symbols: { [key: string]: string } = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'QAR': 'QAR ', 'AED': 'AED ', 'SAR': 'SAR ', 'INR': '₹', 'JPY': '¥', 'CNY': '¥', 'AUD': 'A$', 'CAD': 'C$', 'NZD': 'NZ$', 'SGD': 'S$', 'HKD': 'HK$', 'KRW': '₩', 'BRL': 'R$', 'ZAR': 'R', 'RUB': '₽', 'CHF': 'CHF ', 'SEK': 'kr ', 'NOK': 'kr ', 'DKK': 'kr ', 'PLN': 'zł', 'TRY': '₺', 'MXN': 'Mex$', 'IDR': 'Rp ', 'MYR': 'RM ', 'PHP': '₱', 'THB': '฿', 'VND': '₫', 'EGP': 'E£', 'PKR': '₨', 'BDT': '৳', 'NGN': '₦', 'GHS': 'GH₵', 'KES': 'KSh ', 'UGX': 'USh ', 'TZS': 'TSh ', 'RWF': 'RF ', 'BIF': 'FBu ', 'ETB': 'Br ', 'XOF': 'CFA ', 'XAF': 'FCFA ',
    };
    return symbols[curr] || `${curr} `;
  };
  const formatCurrency = (amount: number) => {
    const symbol = getCurrencySymbol(currency);
    let formattedAmount = amount.toFixed(2);
    switch(currency) {
      case 'JPY': case 'KRW': case 'VND': formattedAmount = Math.round(amount).toString(); break;
      case 'BHD': case 'KWD': case 'OMR': formattedAmount = amount.toFixed(3); break;
    }
    formattedAmount = formattedAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const symbolAfter = ['kr', 'Kč', 'Ft'].some(s => symbol.trim() === s);
    return symbolAfter ? `${formattedAmount} ${symbol}` : `${symbol}${formattedAmount}`;
  };

  return (
    <div id="invoice-template" className="bg-white mx-auto shadow-lg rounded-lg overflow-hidden print:shadow-none" style={{ width: '210mm', minHeight: '297mm', padding: '20mm', margin: '20px auto', boxSizing: 'border-box', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Company Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: company.primaryColor }}>{company.companyName}</h1>
          <div className="text-sm text-gray-600 space-y-1">
            {company.licenseNumber && (<div>License Number: {company.licenseNumber}</div>)}
            <div style={{ whiteSpace: 'pre-line' }}>{company.address}</div>
            <div>Phone: {company.phone}</div>
            {company.mobile && <div>Mobile: {company.mobile}</div>}
            <div>{company.email}</div>
            {company.website && (
              <div>
                <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1" style={{ color: company.primaryColor }}>{company.website}</a>
              </div>
            )}
          </div>
        </div>
        <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
          {company.logo ? (<img src={company.logo} alt={company.companyName} className="max-w-full max-h-full object-contain" />) : (<div className="text-gray-400 text-lg font-bold">LOGO</div>)}
        </div>
      </div>
      <hr className="border-t-2 border-gray-200 mb-6" />
      {/* Bill To and Invoice Details */}
      <div className="flex justify-between mb-8">
        <div className="w-1/2">
          <div className="text-sm font-medium text-gray-500 mb-2">BILL TO</div>
          <div className="text-lg font-bold mb-2">{invoice.client.name}</div>
          <div className="text-sm text-gray-600 space-y-1">
            <div style={{ whiteSpace: 'pre-line' }}>{invoice.client.address}</div>
            {invoice.client.phone && <div>Phone: {invoice.client.phone}</div>}
            <div>{invoice.client.email}</div>
          </div>
          <div className="mt-4">
            <div className="text-sm font-medium text-gray-500">INVOICE</div>
            <div className="text-xl font-bold" style={{ color: company.primaryColor }}>{invoice.invoiceNumber}</div>
          </div>
        </div>
        <div className="w-1/3 text-right">
          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium text-gray-500">DATE</div>
              <div className="text-base">{new Date(invoice.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">DUE DATE</div>
              <div className="text-base">{new Date(invoice.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500">BALANCE DUE</div>
              <div className="text-2xl font-bold" style={{ color: company.primaryColor }}>{formatCurrency(invoice.total)}</div>
            </div>
          </div>
        </div>
      </div>
      {/* Items Table */}
      <div className="mb-8">
        <div className="rounded-t-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-4 p-4 text-sm font-bold text-white" style={{ backgroundColor: company.primaryColor }}>
            <div className="col-span-6">DESCRIPTION</div>
            <div className="col-span-2 text-center">RATE</div>
            <div className="col-span-2 text-center">QTY</div>
            <div className="col-span-2 text-right">AMOUNT</div>
          </div>
        </div>
        <div className="border-x border-gray-200">
          {invoice.items.map((item, index) => (
            <div key={item.id} className={`grid grid-cols-12 gap-4 p-4 text-sm ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
              <div className="col-span-6">{item.description}</div>
              <div className="col-span-2 text-center">{formatCurrency(item.rate)}</div>
              <div className="col-span-2 text-center">{item.quantity}</div>
              <div className="col-span-2 text-right">{formatCurrency(item.amount)}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Totals Section */}
      <div className="flex justify-between items-start mb-8">
        {/* Status Stamp */}
        <div className="w-1/3">
          {invoice.status === 'paid' && (<img src="/paid.png" alt="PAID" className="w-24 transform -rotate-12 opacity-90" />)}
          {(invoice.status === 'overdue' || invoice.status === 'sent') && (<img src="/unpaid.png" alt="UNPAID" className="w-24 transform -rotate-12 opacity-90" />)}
        </div>
        {/* Calculations */}
        <div className="w-1/2 bg-gray-50 rounded-lg p-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal</span>
              <span>{formatCurrency(invoice.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tax ({invoice.taxRate}%)</span>
              <span>{formatCurrency(invoice.taxAmount)}</span>
            </div>
            {invoice.discountAmount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount</span>
                <span>-{formatCurrency(invoice.discountAmount)}</span>
              </div>
            )}
            <div className="border-t border-gray-300 pt-2 mt-2">
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span style={{ color: company.primaryColor }}>{formatCurrency(invoice.total)}</span>
              </div>
            </div>
            {invoice.status === 'paid' && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Paid</span>
                <span>-{formatCurrency(invoice.total)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold">
              <span>Balance Due</span>
              <span style={{ color: company.primaryColor }}>{formatCurrency(invoice.status === 'paid' ? 0 : invoice.total)}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Payment Link Section */}
      {invoice.status !== 'paid' && invoice.paymentLink && (
        <div className="bg-gray-50 p-4 rounded-lg mb-8 border border-gray-200">
          <div className="flex items-center gap-2 mb-1">
            <div className="font-bold" style={{ color: company.primaryColor }}>Pay Online</div>
          </div>
          <div className="text-sm break-all">
            <a href={invoice.paymentLink} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1" style={{ color: company.primaryColor }}>{invoice.paymentLink}</a>
          </div>
        </div>
      )}
      {/* Notes Section */}
      {(invoice.notes || invoice.terms) && (
        <div className="space-y-4 text-sm text-gray-600">
          {invoice.notes && (<div><div className="font-medium text-gray-700 mb-1">Notes</div><div style={{ whiteSpace: 'pre-line' }}>{invoice.notes}</div></div>)}
          {invoice.terms && (<div><div className="font-medium text-gray-700 mb-1">Terms & Conditions</div><div style={{ whiteSpace: 'pre-line' }}>{invoice.terms}</div></div>)}
        </div>
      )}
      <hr className="border-t-2 border-gray-200 my-6" />
      {/* Thank You Message */}
      <div className="text-center">
        <div className="font-bold text-lg mb-1" style={{ color: company.primaryColor }}>Thank you for your business!</div>
        <div className="text-sm text-gray-600">{company.companyName}</div>
      </div>
      {/* Signature */}
      {invoice.signature && (
        <div className="mt-10 text-center">
          <div className="text-xs text-gray-500 mb-1">Authorized Signature</div>
          {invoice.signature.startsWith('http') ? (
            <img src={invoice.signature} alt="Signature" className="mx-auto h-12 object-contain" />
          ) : (
            <div className="italic text-lg font-signature text-gray-700" style={{ fontFamily: 'cursive, serif' }}>{invoice.signature}</div>
          )}
        </div>
      )}
    </div>
  );
} 